import React from 'react';
import { useQuery } from '@apollo/client';
import { StatusLabel } from 'components/Text';
import { LoadingScreen } from 'components/LoadingScreen';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { GET_ORDER_BY_ID } from 'gql/queries';
import { Order } from 'types/order';
import { format } from 'date-fns';
import { useStore } from 'context/store-context';
import { Back } from 'components/Back';
import {
  formatAddress,
  formatCurrency,
  getLastAddressUpdate,
  getStoreCurrencyFormatter,
  wasOrderUpdated,
} from 'utils';
import { withNotification } from 'components/Notification';
import { NotificationTypes } from 'utils';
import OrderRiskAnalysisResult from 'components/OrderRiskAnalysis/OrderRiskAnalysisResult';
import ActivitiesTimeline from 'components/OrderActivitiesTimeline';

import withConfirmation from 'hooks/withConfirmation';
import { ExternalLinkIcon, UserIcon } from '@heroicons/react/solid';
import { OrderItem } from 'components/OrderItem';
import { OrderStatus } from './data/orders-data';
import Panel from 'components/Panel';
import { Button } from 'components/Button';
import { OrderShipmentSummary } from 'components/OrderShipmentSummary';

import {
  getOrderFulfillmentStatus,
  getOrderFulfillmentStatusType,
} from 'utils/orderFulfillmentStatus';
import { OrderRequestsPanel } from 'components/OrderRequestsPanel';
import { OrderReturnExceptionPanel } from 'components/OrderReturnExceptionPanel';
import {
  getRequestReturnTo,
  getShopifyOrderAdminUrl,
} from 'utils/requestNavigation';

export const getOrderStatus = (order: Order) => {
  if (order.cancelledAt) {
    return OrderStatus.CANCELLED;
  } else if (order.claims.length) {
    return OrderStatus.CLAIM_FILED;
  } else if (order.protectionTotal > 0) {
    return OrderStatus.GUARANTEED;
  }
  return OrderStatus.NOT_GUARANTEED;
};

export const ViewOrder = withConfirmation(
  withNotification(({ showNotification, setShowConfirmation }: any) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const returnTo = getRequestReturnTo(
      location.search,
      (location.state as { returnLocation?: string } | null)?.returnLocation,
      '/orders',
    );

    const { storeId, storeProperties, storeDomain } = useStore();
    const { data, loading } = useQuery(GET_ORDER_BY_ID, {
      variables: {
        storeId,
        id: params.orderId,
      },
      onError(error) {
        showNotification(NotificationTypes.error, error?.message);
      },
    });

    const onFileClaim = (order: Order) => {
      navigate('/claims/new-claim', {
        state: { order, returnLocation: returnTo },
      });
    };

    const candidate: Order | undefined = data?.orders?.[0];
    const order =
      candidate?.id === params.orderId && candidate?.storeId === storeId
        ? candidate
        : null;

    if (loading) return <LoadingScreen />;

    if (!order)
      return (
        <div className="max-w-7xl mx-auto p-6">
          <Back returnLocation={returnTo} />
          <p className="mt-4 text-sm text-gray-500">
            Order is unavailable for this store.
          </p>
        </div>
      );

    const lastAddressUpdate = getLastAddressUpdate(order.activities);
    const fulfillmentStatus = getOrderFulfillmentStatus(order.items);
    const shopifyOrderUrl = getShopifyOrderAdminUrl(storeDomain, order.orderId);
    const protectionStatus =
      order.protectionTotal > 0
        ? OrderStatus.GUARANTEED
        : OrderStatus.NOT_GUARANTEED;

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pb-10">
        <div className="max-w-7xl mx-auto bg-white p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <Back className="mr-5" returnLocation={returnTo} />
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-xl font-semibold text-gray-900">
                  {order.number}
                </h1>
                {shopifyOrderUrl && (
                  <Link
                    to={shopifyOrderUrl}
                    aria-label="View order in Shopify"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLinkIcon className="w-4 h-4 text-gray-400" />
                  </Link>
                )}
              </div>
              <div className="flex items-start gap-2 flex-col">
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 ${
                      protectionStatus.bgColor +
                      ' ' +
                      protectionStatus.textColor
                    } text-sm rounded font-bold`}
                  >
                    {protectionStatus.label}
                  </span>
                  {order.cancelledAt && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 text-sm rounded font-bold">
                      Cancelled
                    </span>
                  )}
                  {wasOrderUpdated(order?.activities) && (
                    <span
                      className={`px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded font-bold`}
                    >
                      Order Edited
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-gray-500">
                    Order created at{' '}
                    {format(new Date(order?.createdAt), 'do MMM yyyy hh:mm')}
                  </span>
                  {order.cancelledAt && (
                    <span className="text-sm text-gray-500">
                      Order cancelled at at{' '}
                      {format(
                        new Date(order?.cancelledAt),
                        'do MMM yyyy hh:mm',
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-end gap-2">
                {getOrderStatus(order).label === OrderStatus.GUARANTEED.label &&
                  !order?.claims?.length && (
                    <Button
                      props={{
                        'aria-label': 'File issue',
                      }}
                      onClick={onFileClaim.bind(null, order)}
                    >
                      File issue
                    </Button>
                  )}
              </div>
            </div>
          </div>

          <OrderRequestsPanel orderId={order.id} />
          <OrderReturnExceptionPanel orderId={order.id} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content - Left */}
            <div className="lg:col-span-2 space-y-6">
              {[
                OrderStatus.GUARANTEED.label,
                OrderStatus.CLAIM_FILED.label,
              ].includes(getOrderStatus(order).label) && (
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">
                      Risk Analysis
                    </h3>
                    <UserIcon className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" />
                  </div>

                  <div className="space-y-3">
                    {order.id && (
                      <OrderRiskAnalysisResult orderId={order.id} flex="row" />
                    )}
                  </div>
                </div>
              )}
              <Panel title="Order Items">
                <div className="px-3 pb-3">
                  {order.items.map((item) => (
                    <OrderItem
                      key={item.id}
                      order={item}
                      onRemoveItem={() => {}}
                      isDisplay
                      viewOrder
                    />
                  ))}
                </div>
              </Panel>
              <OrderShipmentSummary
                orderId={order.id}
                trackings={order.trackings}
                items={order.items}
              />
              {order?.activities?.length > 0 && (
                <ActivitiesTimeline
                  activities={order.activities}
                  claims={order.claims}
                />
              )}
            </div>

            {/* Sidebar - Right */}
            <div className="space-y-6">
              {/* Customer */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Customer</h3>
                  <UserIcon className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" />
                </div>

                <div className="space-y-3">
                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Order Number
                    </div>
                    <div className="text-sm text-gray-500">{order.number}</div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Order Date
                    </div>
                    <div className="text-sm text-gray-500">
                      {format(new Date(order?.createdAt), 'do MMM yyyy hh:mm')}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Customer Name
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.customerName}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Email
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.customerEmail ?? 'N/A'}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Phone
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.customerPhone ?? 'N/A'}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Order Total
                    </div>
                    <div className="text-sm text-gray-500">
                      {getStoreCurrencyFormatter(
                        storeProperties?.currency,
                        formatCurrency(order.total ?? 0),
                      )}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700">
                      Guarantee Total
                    </div>
                    <div className="text-sm text-gray-500">
                      {getStoreCurrencyFormatter(
                        storeProperties?.currency,
                        order.total,
                      )}
                    </div>
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700 mb-2">
                      Fulfillment Status
                    </div>
                    <StatusLabel
                      label={fulfillmentStatus}
                      type={getOrderFulfillmentStatusType(fulfillmentStatus)}
                    />
                  </div>

                  <div className="border-b border-gray-200 py-2">
                    <div className="text-sm font-semibold text-gray-700 items-center">
                      Shipping Address{' '}
                      {lastAddressUpdate ? (
                        <span className="text-xs text-blue-700">
                          (Edited on{' '}
                          {format(
                            new Date(lastAddressUpdate),
                            'MM/dd/yyyy hh:mm a',
                          )}{' '}
                          )
                        </span>
                      ) : (
                        ''
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatAddress(order.shippingAddress)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }),
);
